// Every market Hyperliquid lists: the main dex's perps and every builder-deployed (HIP-3) dex's,
// each with its day context, its asset class and the canonical underlying the newsroom names it
// by. Read from `/info` directly (it answers CORS with `*`), every 30 s.

import { useQuery } from "@tanstack/react-query";
import { canonical, classOf, type AssetClass } from "../../shared/newsroom/index.ts";
import { INFO_URL } from "../hl/info";

export type Market = {
  /** Hyperliquid's name for it: "BTC", "kPEPE", "xyz:NVDA". */
  coin: string;
  /** "" for the main dex. */
  dex: string;
  /** What the page shows: "BTC", "PEPE", "NVDA". */
  symbol: string;
  /** The newsroom's name for what it tracks: "BTC", "PEPE", "NVDA", "XAU". */
  underlying: string;
  class: AssetClass;
  /** Price decimals Hyperliquid quotes it at, at most. */
  pxDecimals: number;
  maxLeverage: number;
  /** As Hyperliquid writes them. */
  mark: string;
  prevDay: string;
  oraclePx: string;
  funding: string;
  /** For ranking and display only: never counted. */
  dayVolumeUsd: number;
  openInterestUsd: number;
  change24h: number;
};

type Meta = { universe: { name: string; szDecimals: number; maxLeverage: number; isDelisted?: boolean }[] };
type Ctx = { markPx: string; prevDayPx: string; oraclePx: string; dayNtlVlm: string; openInterest: string; funding: string; midPx?: string | null };

async function info<T>(body: unknown): Promise<T> {
  const response = await fetch(INFO_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!response.ok) throw new Error(`Hyperliquid answered ${response.status}`);
  return (await response.json()) as T;
}

/** Builder dexes whose listings say what they are better than the ticker does. */
const DEX_CLASS: Record<string, AssetClass> = { mkts: "indices" };

function classify(dex: string, symbol: string, underlying: string): AssetClass {
  if (!dex) return "crypto";
  const known = classOf(underlying) ?? classOf(symbol);
  if (known) return known;
  if (/^\d+Y$/.test(symbol)) return "rates";
  if (/^(BTCD|TOTAL\d?|ETHBTC|OTHERS)$/.test(symbol)) return "crypto";
  return DEX_CLASS[dex] ?? "equities";
}

function marketsOf(dex: string, meta: Meta, ctxs: Ctx[]): Market[] {
  const out: Market[] = [];
  meta.universe.forEach((asset, index) => {
    const ctx = ctxs[index];
    if (!ctx || asset.isDelisted) return;
    const dayVolumeUsd = Number(ctx.dayNtlVlm);
    const mark = Number(ctx.markPx);
    if (!(mark > 0)) return;
    const symbol = dex ? asset.name.slice(dex.length + 1) : asset.name.replace(/^k(?=[A-Z]{2,})/, "");
    const underlying = canonical(asset.name);
    const prev = Number(ctx.prevDayPx);
    out.push({
      coin: asset.name,
      dex,
      symbol,
      underlying,
      class: classify(dex, symbol, underlying),
      pxDecimals: Math.max(0, (dex ? 6 : 6) - asset.szDecimals),
      maxLeverage: asset.maxLeverage,
      mark: ctx.markPx,
      prevDay: ctx.prevDayPx,
      oraclePx: ctx.oraclePx,
      funding: ctx.funding,
      dayVolumeUsd: Number.isFinite(dayVolumeUsd) ? dayVolumeUsd : 0,
      openInterestUsd: Number(ctx.openInterest) * mark,
      change24h: prev > 0 ? mark / prev - 1 : 0,
    });
  });
  return out;
}

export type Universe = {
  at: number;
  markets: Market[];
  byCoin: Map<string, Market>;
  /** Markets by canonical underlying, most traded first. */
  byUnderlying: Map<string, Market[]>;
};

export async function loadUniverse(): Promise<Universe> {
  const dexes = await info<({ name: string } | null)[]>({ type: "perpDexs" });
  const names = ["", ...dexes.filter((dex): dex is { name: string } => dex !== null).map((dex) => dex.name)];
  const reads = await Promise.allSettled(
    names.map(async (dex) => {
      const [meta, ctxs] = await info<[Meta, Ctx[]]>(dex ? { type: "metaAndAssetCtxs", dex } : { type: "metaAndAssetCtxs" });
      return marketsOf(dex, meta, ctxs);
    }),
  );
  const markets = reads.flatMap((read) => (read.status === "fulfilled" ? read.value : []));
  if (!markets.length) throw new Error("Hyperliquid listed no markets");
  markets.sort((a, b) => b.dayVolumeUsd - a.dayVolumeUsd);
  const byCoin = new Map(markets.map((market) => [market.coin, market]));
  const byUnderlying = new Map<string, Market[]>();
  for (const market of markets) {
    const list = byUnderlying.get(market.underlying) ?? [];
    list.push(market);
    byUnderlying.set(market.underlying, list);
  }
  return { at: Date.now(), markets, byCoin, byUnderlying };
}

export function useUniverse() {
  return useQuery({ queryKey: ["universe"], queryFn: loadUniverse, refetchInterval: 30_000, staleTime: 15_000 });
}

/** The most traded market for an underlying, if Hyperliquid lists one with real volume. */
export function primary(universe: Universe | undefined, underlying: string): Market | undefined {
  return universe?.byUnderlying.get(canonical(underlying))?.[0];
}

export const CLASS_LABEL: Record<AssetClass, string> = {
  crypto: "Crypto",
  equities: "Stocks",
  commodities: "Commodities",
  fx: "FX",
  rates: "Rates",
  indices: "Indices",
};

/** Where to trade it on Hyperliquid itself. */
export function hyperliquidUrl(market: Market): string {
  return `https://app.hyperliquid.xyz/trade/${encodeURIComponent(market.coin)}`;
}
